import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import CategoryOutlined from "@mui/icons-material/CategoryOutlined";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import {
  Box,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { docUploadModalOpen } from "../../../../redux/actions/docUploadModalActions";
import {
  TabGhostButton,
  TabPrimaryButton,
} from "../../shared/tabLayout/tabButtons";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { getExpirationDaysForDocType } from "../../../../constants/documents/docTypeExpirationDurations";
import LoanDocumentGroupIcon from "../../shared/LoanDocumentGroupIcon";
import { isDocHidden } from "./LoanDocumentManagerTable";
import { useUploadQueue } from "./UploadQueueContext";

// Card-based "Missing documents" panel — checklist requirements grouped by
// Category (default) or Stage, each row offering an inline upload (button or
// drag-drop) that prepopulates the required docGroup + docType. Rows come from
// `GET /checklists/check_missing_documents/{dealId}`.

const STAGE_ORDER = [
  "Prior to Processing",
  "Prior to Pre-Underwriting",
  "Prior to Appraisal Ordered",
  "Prior to Underwriting",
  "Prior to Docs",
];

const TEAL = "#0d9488";
const TEAL_DARK = "#0f766e";

const strip = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/* ---------------------- missing docs as email text ------------------------- */
// Mirrors the conditions checklist email export: turns the currently-grouped
// outstanding requirements into titled, numbered sections (rich HTML + plain
// text) that can be copied or opened in an email client.

const escHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// One line per requirement — the doc type, plus the still-needed count when a
// requirement needs more than one document.
const emailLineFor = (it) => {
  const outstanding = Math.max(0, (it.needed || 0) - (it.received || 0));
  return outstanding > 1 ? `${it.docType} (need ${outstanding})` : it.docType;
};

// `groups` already reflects the on-screen grouping (By category / By stage), so
// the email matches what the user sees. Only outstanding items are included.
const buildMissingDocsEmail = (groups, loan) => {
  const sections = (groups || [])
    .map((g) => ({
      label: g.key,
      titles: g.items
        .filter((it) => (it.received || 0) < (it.needed || 0))
        .map(emailLineFor),
    }))
    .filter((s) => s.titles.length > 0);

  const loanLabel = loan?.loanName || loan?.loanSyncId || loan?.loanNumber;
  const subject = loanLabel
    ? `Missing Documents — ${loanLabel}`
    : "Missing Documents";

  const htmlParts = [
    `<p style="margin:0 0 8px">Hi Team,</p>`,
    `<p style="margin:0 0 4px">Please see the outstanding documents needed for this loan below:</p>`,
  ];
  sections.forEach((s) => {
    htmlParts.push(
      `<p style="margin:12px 0 4px"><u><b>${escHtml(s.label)}:</b></u></p>`,
    );
    htmlParts.push(
      `<ol style="margin:0 0 8px 0;padding-left:24px">${s.titles
        .map((t) => `<li style="margin:2px 0">${escHtml(t)}</li>`)
        .join("")}</ol>`,
    );
  });
  const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1e293b">${htmlParts.join(
    "",
  )}</div>`;

  const textParts = [
    "Hi Team,",
    "",
    "Please see the outstanding documents needed for this loan below:",
    "",
  ];
  sections.forEach((s) => {
    textParts.push(`${s.label}:`);
    s.titles.forEach((t, i) => textParts.push(`  ${i + 1}. ${t}`));
    textParts.push("");
  });
  const text = textParts.join("\n").trim();

  return { html, text, subject };
};

// Outlook on the web compose tab (M365 / work accounts).
const OUTLOOK_WEB_COMPOSE = "https://outlook.office.com/mail/deeplink/compose";
const openOutlookWebEmail = ({ subject, text }) => {
  const url = `${OUTLOOK_WEB_COMPOSE}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

// The OS default mail client (Outlook desktop, Mail, etc.) via mailto:.
const openDefaultEmailClient = ({ subject, text }) => {
  const url = `mailto:?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(text)}`;
  window.open(url, "_self");
};

// Write rich HTML + plain text to the clipboard (formatting survives a paste
// into an email client); falls back to plain text where unsupported.
const copyEmailToClipboard = async ({ html, text }) => {
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
    } else {
      await navigator.clipboard.writeText(text);
    }
    return true;
  } catch {
    return false;
  }
};

// ── Progress ring ───────────────────────────────────────────────────────────
const ProgressRing = ({ pct, size = 56 }) => {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth="5"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={TEAL}
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        style={{ transition: "stroke-dashoffset 500ms ease" }}
      />
    </svg>
  );
};

// ── Count pill (received of needed) ──────────────────────────────────────────
const CountPill = ({ received, needed }) => {
  const done = received >= needed;
  const started = received > 0 && !done;
  const tone = done
    ? { fg: "#047857", bg: "#ecfdf5" }
    : started
      ? { fg: "#b45309", bg: "#fffbeb" }
      : { fg: "#dc2626", bg: "#fef2f2" };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        borderRadius: "999px",
        px: 1,
        py: 0.15,
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: tone.fg,
        background: tone.bg,
      }}
    >
      {done ? (
        <CheckCircleRounded sx={{ fontSize: 12 }} />
      ) : (
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: tone.fg,
          }}
        />
      )}
      {received} of {needed}
    </Box>
  );
};

// ── Requirement row ──────────────────────────────────────────────────────────
const RequirementRow = ({ req, onUploadClick, onDropFiles, groupBy }) => {
  const [dragOver, setDragOver] = useState(false);
  const done = req.received >= req.needed;
  const outstanding = req.needed - req.received;

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        if (!done) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (!done && e.dataTransfer?.files?.length)
          onDropFiles(req, e.dataTransfer.files);
      }}
      sx={{
        borderBottom: "1px solid #f1f5f9",
        "&:last-of-type": { borderBottom: "none" },
        transition: "background 120ms",
        ...(dragOver
          ? {
              background: "rgba(13,148,136,0.08)",
              boxShadow: "inset 0 0 0 2px #2dd4bf",
              borderRadius: "8px",
            }
          : done
            ? { background: "rgba(16,185,129,0.06)" }
            : { "&:hover": { background: "rgba(248,250,252,0.8)" } }),
      }}
    >
      <Flx ac g={1.5} sx={{ px: 2, py: 1.5 }}>
        <LoanDocumentGroupIcon docGroup={req.category} styled size={34} />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Flx ac wrap g={1}>
            {/* When grouped by stage, the category isn't the section header, so
                surface it here as a chip; otherwise the section header already
                carries it and we lead with the doc type. */}
            {groupBy === "stage" && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  borderRadius: "6px",
                  px: 0.75,
                  py: 0.15,
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: "#475569",
                  background: "#f1f5f9",
                  whiteSpace: "nowrap",
                }}
              >
                {req.category}
              </Box>
            )}
            <Txt
              sx={{
                fontSize: 13.5,
                fontWeight: 600,
                color: done ? "#94a3b8" : "#1e293b",
                textDecoration: done ? "line-through" : "none",
              }}
            >
              {req.docType}
            </Txt>
            <CountPill received={req.received} needed={req.needed} />
          </Flx>
          <Flx ac wrap g={1} sx={{ mt: 0.25 }}>
            {/* When grouped by category the stage isn't the section header, so
                surface it here as a small, subtle tag. */}
            {/* Temporarily hidden. */}
            {false &&
              groupBy === "category" &&
              req.stage &&
              req.stage !== "Other" && (
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    borderRadius: "5px",
                    px: 0.6,
                    py: 0.1,
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#94a3b8",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    whiteSpace: "nowrap",
                  }}
                >
                  {req.stage}
                </Box>
              )}
            {/* The requirement text now lives inline (row is no longer
                expandable). */}
            <Txt sx={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.5 }}>
              {req.requirement}
            </Txt>
            {!done && outstanding > 1 && (
              <Txt sx={{ fontSize: 11.5, color: "#94a3b8" }}>
                · {outstanding} still needed
              </Txt>
            )}
            {dragOver && (
              <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: TEAL_DARK }}>
                Drop to upload
              </Txt>
            )}
          </Flx>
        </Box>

        {done ? (
          <Box
            sx={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              borderRadius: "999px",
              background: "#d1fae5",
              px: 1.5,
              py: 0.75,
              fontSize: 11,
              fontWeight: 700,
              color: "#047857",
            }}
          >
            <CheckCircleRounded sx={{ fontSize: 14 }} /> Complete
          </Box>
        ) : (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onUploadClick(req);
            }}
            startIcon={<FileUploadOutlined sx={{ fontSize: 14 }} />}
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontSize: 11.5,
              fontWeight: 700,
              px: 1.5,
              py: 0.75,
              borderRadius: "8px",
              bgcolor: TEAL,
              color: "#ffffff",
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
              "& .MuiButton-startIcon": { mr: 0.5 },
              "&:hover": { bgcolor: TEAL_DARK },
            }}
          >
            Upload
          </Button>
        )}
      </Flx>
    </Box>
  );
};

// ── Main panel ───────────────────────────────────────────────────────────────
const MissingDocumentsTable = ({ rowData, loan, onUploaded }) => {
  // Both the Upload button and drag-drop stage files into the shared, bottom-
  // docked upload queue (same panel the top dropzones feed) rather than
  // uploading immediately.
  const { onFilesAdded } = useUploadQueue();
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [groupBy, setGroupBy] = useState("category"); // default: By Category
  // Email-export menu + "Copied" confirmation.
  const [emailAnchor, setEmailAnchor] = useState(null);
  // Upload popover (Quick Upload / By group → the global upload modal).
  const [uploadAnchor, setUploadAnchor] = useState(null);
  const [copied, setCopied] = useState(false);
  // Collapsed section keys (by group key). Expanded by default.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  // A single hidden file input, retargeted per row: the Upload button records
  // its requirement then opens the OS picker; the chosen files are staged.
  const fileInputRef = useRef(null);
  const pendingReqRef = useRef(null);

  const items = useMemo(
    () =>
      (rowData || []).map((m) => ({
        id: m.oid || m._id,
        category: m.docGroup || "Uncategorized",
        docType: m.docType || "Document",
        received: typeof m.presentCount === "number" ? m.presentCount : 0,
        needed: typeof m.requiredCount === "number" ? m.requiredCount : 1,
        stage: m.contingentStage || "Other",
        requirement: m.description || strip(m.htmlString),
        fileIds: m.uploadedDocumentIds || [],
      })),
    [rowData],
  );

  const totals = useMemo(() => {
    const total = items.length;
    const complete = items.filter((r) => r.received >= r.needed).length;
    const partial = items.filter(
      (r) => r.received > 0 && r.received < r.needed,
    ).length;
    const notStarted = total - complete - partial;
    const docsNeeded = items.reduce(
      (s, r) => s + Math.max(0, r.needed - r.received),
      0,
    );
    // The endpoint only returns OUTSTANDING requirements — satisfied ones drop
    // off entirely — so `complete / total` is ~always 0. Instead estimate
    // progress from the loan's actual uploaded (non-hidden) documents against
    // what's still needed: present / (present + needed). This trends up as
    // docs come in and reaches 100% once nothing is outstanding.
    const docsPresent = (loan?.loanDocuments || []).filter(
      (d) => !isDocHidden(d),
    ).length;
    const denom = docsPresent + docsNeeded;
    return {
      total,
      complete,
      partial,
      notStarted,
      docsNeeded,
      docsPresent,
      pct: denom ? docsPresent / denom : 1,
    };
  }, [items, loan?.loanDocuments]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((r) => {
      if (
        q &&
        !`${r.docType} ${r.category} ${r.requirement}`.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [items, query]);

  const groups = useMemo(() => {
    if (groupBy === "stage") {
      const known = STAGE_ORDER.map((s) => ({
        key: s,
        items: visible.filter((r) => r.stage === s),
      }));
      const extraKeys = [
        ...new Set(
          visible.map((r) => r.stage).filter((s) => !STAGE_ORDER.includes(s)),
        ),
      ].sort();
      const extras = extraKeys.map((s) => ({
        key: s,
        items: visible.filter((r) => r.stage === s),
      }));
      return [...known, ...extras].filter((g) => g.items.length);
    }
    const cats = [...new Set(visible.map((r) => r.category))].sort();
    return cats.map((c) => ({
      key: c,
      items: visible.filter((r) => r.category === c),
    }));
  }, [visible, groupBy]);

  // Email export — grouped exactly as shown (By category / By stage).
  const hasMissing = items.some((r) => (r.received || 0) < (r.needed || 0));

  const handleCopyEmail = useCallback(async () => {
    if (!hasMissing) return;
    const ok = await copyEmailToClipboard(buildMissingDocsEmail(groups, loan));
    if (ok) setCopied(true);
  }, [groups, loan, hasMissing]);

  const handleOpenEmail = useCallback(
    (opener) => {
      setEmailAnchor(null);
      if (!hasMissing) return;
      opener(buildMissingDocsEmail(groups, loan));
    },
    [groups, loan, hasMissing],
  );

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  const subtitleParts = [
    loan?.loanSyncId,
    loan?.loanProductType || loan?.loanPurpose,
    typeof loan?.totalLoanAmount === "number"
      ? formatDollar(loan.totalLoanAmount)
      : null,
  ].filter(Boolean);

  // Stage the given files against a requirement into the shared upload queue,
  // prefilled with that requirement's docGroup/docType (and auto validity
  // window). Used by BOTH drag-drop and the Upload button.
  const stageFiles = (req, fileList) => {
    if (!fileList?.length) return;
    const raw = (rowData || []).find((m) => (m.oid || m._id) === req.id);
    const docType = raw?.docType || req.docType || undefined;
    const days = docType ? getExpirationDaysForDocType(docType) : null;
    const files = Array.from(fileList).map((file) => ({
      id: uuidv4(),
      file,
      name: file.name,
      file_display_name: file.name,
      docGroup: raw?.docGroup || req.category || undefined,
      docType,
      reportGuidelineExpirationDays: days ?? null,
    }));
    onFilesAdded(files);
    onUploaded?.();
  };

  // Upload button → open the OS file picker for this requirement.
  const handleUploadClick = (req) => {
    pendingReqRef.current = req;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const req = pendingReqRef.current;
    if (req) stageFiles(req, e.target.files);
    pendingReqRef.current = null;
    e.target.value = ""; // allow re-selecting the same file
  };

  return (
    // Standard drilldown-tab canvas: gray page background (inherited), the
    // 36px/8px canvas inset, and a max-width content column. The header sits on
    // the gray with no white card; only the requirement group cards are white.
    <Box sx={{ width: "100%", py: "36px", px: "8px" }}>
      <Box sx={{ width: "100%", maxWidth: "1660px", margin: "0 auto" }}>
        <Flx ac wrap g={2} sx={{ mb: "18px" }}>
          <Box
            sx={{ position: "relative", display: "flex" }}
            title={`Estimated document completeness: ${totals.docsPresent} uploaded of ~${
              totals.docsPresent + totals.docsNeeded
            } expected (${totals.docsNeeded} still needed).`}
          >
            <ProgressRing pct={totals.pct} size={56} />
            <Txt
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#334155",
              }}
            >
              {Math.round(totals.pct * 100)}%
            </Txt>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Txt
              sx={{
                fontSize: 21,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "#0f172a",
              }}
            >
              Missing documents
            </Txt>
            <Txt sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
              {totals.docsNeeded === 0
                ? "All requirements satisfied — this loan is ready to move forward."
                : `${totals.docsNeeded} file${
                    totals.docsNeeded !== 1 ? "s" : ""
                  } still needed across ${totals.total - totals.complete} requirement${
                    totals.total - totals.complete !== 1 ? "s" : ""
                  }${subtitleParts.length ? " · " + subtitleParts.join(" · ") : ""}`}
            </Txt>
          </Box>

          {/* Header actions — the drilldown standard pair: a ghost (Email) +
              the primary action (Upload). Same geometry as every tab header. */}
          <Flx ac g={1} sx={{ flexShrink: 0 }}>
            <Tooltip
              title="Copy or open the missing documents list as an email"
              placement="top"
            >
              <span>
                <TabGhostButton
                  label={copied ? "Copied" : "Email"}
                  icon={copied ? <CheckRounded /> : <EmailOutlined />}
                  endIcon={<KeyboardArrowDownRounded />}
                  onClick={(e) => setEmailAnchor(e.currentTarget)}
                  disabled={!hasMissing}
                />
              </span>
            </Tooltip>
            <TabPrimaryButton
              label="Upload"
              icon={<FileUploadOutlined />}
              endIcon={<KeyboardArrowDownRounded />}
              onClick={(e) => setUploadAnchor(e.currentTarget)}
            />
          </Flx>
        </Flx>
        {/* Search + grouping toggle — aligned with the groups below. */}
        <Flx ac wrap g={1.5} sx={{ mb: 2 }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search document types or requirements…"
            size="small"
            sx={{
              flex: 1,
              minWidth: 200,
              "& .MuiOutlinedInput-root": {
                background: "#f8fafc",
                borderRadius: "8px",
                fontSize: 13,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
          {/* By stage / By category toggle (replaces the status filter) */}
          <Flx
            sx={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              p: 0.25,
            }}
          >
            {[
              { key: "stage", label: "By stage", icon: AccountTreeOutlined },
              { key: "category", label: "By category", icon: CategoryOutlined },
            ].map((g) => {
              const active = groupBy === g.key;
              const Icon = g.icon;
              return (
                <Box
                  key={g.key}
                  component="button"
                  onClick={() => setGroupBy(g.key)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    borderRadius: "6px",
                    px: 1.5,
                    py: 0.75,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 11.5,
                    fontWeight: 700,
                    background: active ? "#ffffff" : "transparent",
                    color: active ? "#1e293b" : "#64748b",
                    boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                    "&:hover": { color: active ? "#1e293b" : "#334155" },
                  }}
                >
                  <Icon sx={{ fontSize: 14 }} /> {g.label}
                </Box>
              );
            })}
          </Flx>
        </Flx>
        <Menu
          anchorEl={emailAnchor}
          open={Boolean(emailAnchor)}
          onClose={() => setEmailAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              setEmailAnchor(null);
              handleCopyEmail();
            }}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <ContentCopyRounded sx={{ fontSize: 16 }} />
            Copy formatted list
          </MenuItem>
          <MenuItem
            onClick={() => handleOpenEmail(openOutlookWebEmail)}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <EmailOutlined sx={{ fontSize: 16 }} />
            Open in Outlook (web)
          </MenuItem>
          <MenuItem
            onClick={() => handleOpenEmail(openDefaultEmailClient)}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <EmailOutlined sx={{ fontSize: 16 }} />
            Open in default email app
          </MenuItem>
        </Menu>
        {/* Upload popover — opens the global reusable upload modal on the
            chosen tab (Quick Upload dropzone / Upload By Category grid). */}
        <Menu
          anchorEl={uploadAnchor}
          open={Boolean(uploadAnchor)}
          onClose={() => setUploadAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              setUploadAnchor(null);
              dispatch(docUploadModalOpen(null, { initialView: "quick_upload" }));
            }}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <FileUploadOutlined sx={{ fontSize: 16 }} />
            Quick Upload
          </MenuItem>
          <MenuItem
            onClick={() => {
              setUploadAnchor(null);
              dispatch(docUploadModalOpen(null, { initialView: "group" }));
            }}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <CategoryOutlined sx={{ fontSize: 16 }} />
            By group
          </MenuItem>
        </Menu>
        {/* Groups */}
        {groups.length === 0 ? (
          <Flx
            column
            ac
            center
            g={1}
            sx={{
              borderRadius: "16px",
              border: "1px dashed #cbd5e1",
              background: "#fff",
              p: 5,
              textAlign: "center",
            }}
          >
            <CheckCircleRounded sx={{ fontSize: 28, color: "#10b981" }} />
            <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
              Nothing matches this view
            </Txt>
            <Txt sx={{ fontSize: 13, color: "#64748b" }}>
              Try a different filter or clear your search.
            </Txt>
          </Flx>
        ) : (
          <Flx column g={2}>
            {groups.map((g) => {
              const done = g.items.filter((r) => r.received >= r.needed).length;
              const collapsed = collapsedGroups.has(g.key);
              return (
                <Box
                  key={g.key}
                  sx={{
                    overflow: "hidden",
                    borderRadius: "16px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <Flx
                    ac
                    g={1.5}
                    onClick={() => toggleGroup(g.key)}
                    sx={{
                      borderBottom: collapsed ? "none" : "1px solid #f1f5f9",
                      background: "rgba(248,250,252,0.6)",
                      px: 2,
                      py: 1.25,
                      cursor: "pointer",
                      "&:hover": { background: "rgba(241,245,249,0.9)" },
                    }}
                  >
                    {groupBy === "stage" && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: "#e2e8f0",
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#475569",
                          flexShrink: 0,
                        }}
                      >
                        {STAGE_ORDER.indexOf(g.key) >= 0
                          ? STAGE_ORDER.indexOf(g.key) + 1
                          : "•"}
                      </Box>
                    )}
                    <Txt
                      sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}
                    >
                      {g.key}
                    </Txt>
                    <Txt
                      sx={{
                        fontSize: 11.5,
                        color: "#94a3b8",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {done}/{g.items.length} complete
                    </Txt>
                    {/* Expand / collapse the section (replaces the always-empty
                        progress bar — satisfied docs drop off this list). */}
                    <KeyboardArrowDown
                      sx={{
                        ml: "auto",
                        fontSize: 22,
                        color: "#94a3b8",
                        transition: "transform 150ms",
                        transform: collapsed ? "rotate(-90deg)" : "none",
                      }}
                    />
                  </Flx>
                  {!collapsed &&
                    g.items.map((r) => (
                      <RequirementRow
                        key={r.id}
                        req={r}
                        groupBy={groupBy}
                        onUploadClick={handleUploadClick}
                        onDropFiles={stageFiles}
                      />
                    ))}
                </Box>
              );
            })}
          </Flx>
        )}

        {/* Single hidden picker, retargeted per row by the Upload button. */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={handleFileInputChange}
        />
      </Box>
    </Box>
  );
};

export default MissingDocumentsTable;
